import { Profile } from '../types/database.types'
import { TASK_STATUSES, TaskStatus } from './project-config'

export const APP_USER_ROLES = ['admin', 'dev', 'sales', 'support'] as const

export type AppUserRole = typeof APP_USER_ROLES[number]

export const PROFILE_CAPABILITY_FLAGS = [
  'quote_first',
  'is_tester',
  'is_2nd_tester',
  'is_coder',
  'is_task_approver',
  'is_finance_approver',
] as const

export type ProfileCapabilityFlag = typeof PROFILE_CAPABILITY_FLAGS[number]

type StatusVisibilityRule = {
  roles: readonly AppUserRole[]
  requireAllCapabilities?: readonly ProfileCapabilityFlag[]
  requireAnyCapabilities?: readonly ProfileCapabilityFlag[]
}

export const TASK_STATUS_VISIBILITY: Record<TaskStatus, readonly StatusVisibilityRule[]> = {
  pending_quote: [
    { roles: ['sales', 'support', 'admin'] },
  ],
  pending_finance_approval: [
    { roles: ['sales', 'support', 'admin'] },
  ],
  in_dev_progress: [
    { roles: ['sales', 'support', 'admin'] },
    { roles: ['dev'], requireAnyCapabilities: ['is_coder'] },
  ],
  released_to_dev: [
    { roles: ['sales', 'support', 'admin'] },
    { roles: ['dev'], requireAnyCapabilities: ['is_coder']  },
  ],
  in_dev_testing: [
    { roles: ['sales', 'support', 'admin'] },
    { roles: ['dev'], requireAnyCapabilities: ['is_tester'] },
  ],
  ready_for_uat: [
    { roles: ['sales', 'support', 'admin'] },
    { roles: ['dev'], requireAnyCapabilities: ['is_2nd_tester'] },
  ],
  released_to_uat: [
    { roles: ['sales', 'support', 'admin'] },
    { roles: ['dev'], requireAnyCapabilities: ['is_2nd_tester'] },
  ],
  in_uat_testing: [
    { roles: ['sales', 'support', 'admin'] },
    { roles: ['dev'], requireAnyCapabilities: ['is_2nd_tester'] },
  ],
  on_hold: [
    { roles: ['sales', 'support', 'admin'] },
  ],
  ready_for_prod: [
    { roles: ['sales', 'support', 'admin'] },
  ],
  released_to_prod: [
    { roles: ['sales', 'support', 'admin'] },
  ],
  completed: [
    { roles: ['sales', 'support', 'admin'] },
  ],
}

const isKnownAppUserRole = (role: string | null | undefined): role is AppUserRole =>
  Boolean(role && APP_USER_ROLES.includes(role as AppUserRole))

const hasAllCapabilities = (profile: Profile, capabilities: readonly ProfileCapabilityFlag[]) =>
  capabilities.every((capability) => Boolean(profile[capability]))

const hasAnyCapabilities = (profile: Profile, capabilities: readonly ProfileCapabilityFlag[]) =>
  capabilities.some((capability) => Boolean(profile[capability]))

const doesProfileMatchRule = (profile: Profile, rule: StatusVisibilityRule) => {
  const role = profile.role as AppUserRole

  if (!rule.roles.includes(role)) {
    return false
  }

  if (rule.requireAllCapabilities?.length && !hasAllCapabilities(profile, rule.requireAllCapabilities)) {
    return false
  }

  if (rule.requireAnyCapabilities?.length && !hasAnyCapabilities(profile, rule.requireAnyCapabilities)) {
    return false
  }

  return true
}

export const canProfileSeeStatus = (profile: Profile | null | undefined, status: string) => {
  if (!(TASK_STATUSES as readonly string[]).includes(status)) {
    return true
  }

  if (!profile || !isKnownAppUserRole(profile.role)) {
    return true
  }

  const rules = TASK_STATUS_VISIBILITY[status as TaskStatus]

  return rules.some((rule) => doesProfileMatchRule(profile, rule))
}

export const getVisibleStatusesForProfile = (profile: Profile | null | undefined): TaskStatus[] => {
  if (!profile || !isKnownAppUserRole(profile.role)) {
    return [...TASK_STATUSES]
  }

  return TASK_STATUSES.filter((status) => canProfileSeeStatus(profile, status))
}